import type { ProductItem, ProductV2 } from "@autumn/shared";
import {
	PackageIcon,
	PencilSimpleIcon,
	PuzzlePieceIcon,
	XIcon,
} from "@phosphor-icons/react";
import { Badge } from "@/components/v2/badges/Badge";
import { Button } from "@/components/v2/buttons/Button";
import { PriceDisplay } from "@/components/forms/update-subscription-v2/components/PriceDisplay";
import { SearchableSelect } from "@/components/v2/selects/SearchableSelect";
import { useOrg } from "@/hooks/common/useOrg";
import { cn } from "@/lib/utils";
import { useCreateScheduleFormContext } from "../context/CreateScheduleFormProvider";
import { CopyFromPreviousPhaseButton } from "./CopyFromPreviousPhaseButton";

export function getSchedulePlanPriceProduct({
	product,
	customItems,
}: {
	product: ProductV2;
	customItems?: ProductItem[] | null;
}): ProductV2 {
	return {
		...product,
		items: customItems ?? product.items,
	};
}

export function SchedulePlanRow({
	phaseIndex,
	planIndex,
	usedKeys,
}: {
	phaseIndex: number;
	planIndex: number;
	usedKeys: Set<string>;
}) {
	const {
		form,
		formValues,
		products,
		handleRemovePlan,
		isPhaseLocked,
		setEditingPlan,
	} = useCreateScheduleFormContext();
	const { org } = useOrg();

	const plan = formValues.phases[phaseIndex]?.plans[planIndex];
	if (!plan) return null;
	const isLocked = isPhaseLocked({ phaseIndex });

	const availableProducts = products.filter((p) => !p.archived);
	const selectedProduct = products.find((p) => p.id === plan.productId);
	const hasCustomizations = plan.isCustom;
	const priceProduct = selectedProduct
		? getSchedulePlanPriceProduct({
				product: selectedProduct,
				customItems: plan.items,
			})
		: null;

	const selectedProductIdsInPhase = new Set(
		formValues.phases[phaseIndex]?.plans
			.filter((_, i) => i !== planIndex)
			.map((p) => p.productId)
			.filter(Boolean),
	);

	const handleProductChange = (productId: string) => {
		form.setFieldValue(
			`phases[${phaseIndex}].plans[${planIndex}].productId`,
			productId,
		);
		form.setFieldValue(
			`phases[${phaseIndex}].plans[${planIndex}].prepaidOptions`,
			{},
		);
		form.setFieldValue(`phases[${phaseIndex}].plans[${planIndex}].items`, null);
		form.setFieldValue(
			`phases[${phaseIndex}].plans[${planIndex}].version`,
			undefined,
		);
	};

	const handleEditClick = () => {
		setEditingPlan({ phaseIndex, planIndex });
	};

	if (!plan.productId) {
		return (
			<div className={cn("group relative", isLocked && "opacity-60")}>
				<SearchableSelect
					value={plan.productId || null}
					onValueChange={handleProductChange}
					options={availableProducts}
					getOptionValue={(product) => product.id}
					getOptionLabel={(product) => product.name}
					getOptionDisabled={(product) =>
						usedKeys.has(product.group ?? product.id)
					}
					renderOption={(product) => (
						<>
							<span className="flex-1 truncate min-w-0">{product.name}</span>
							{selectedProductIdsInPhase.has(product.id) && (
								<span className="text-xs text-subtle shrink-0">
									Already selected
								</span>
							)}
							{!selectedProductIdsInPhase.has(product.id) &&
								usedKeys.has(product.group ?? product.id) && (
									<span className="text-xs text-subtle shrink-0">
										Group conflict
									</span>
								)}
						</>
					)}
					header={
						<CopyFromPreviousPhaseButton phaseIndex={phaseIndex} />
					}
					placeholder="Select product..."
					searchable
					searchPlaceholder="Search products..."
					emptyText="No products found"
					defaultOpen
					disabled={isLocked}
				/>
			</div>
		);
	}

	const row = (
		<div
			className={cn(
				"group flex h-input min-w-0 w-full items-center gap-2 rounded-lg input-base input-shadow-default px-3 text-sm text-foreground",
				isLocked && "opacity-60",
			)}
		>
			{selectedProduct?.is_add_on ? (
				<PuzzlePieceIcon className="size-3.5 shrink-0 text-tertiary-foreground" />
			) : (
				<PackageIcon className="size-3.5 shrink-0 text-tertiary-foreground" />
			)}
			<span className="flex-1 truncate min-w-0">
				{selectedProduct?.name ?? plan.productId}
			</span>
			<div className="relative flex shrink-0 items-center gap-1.5 min-w-[60px] justify-end">
				<div
					className={cn(
						"flex items-center gap-1.5 transition-opacity duration-150",
						!isLocked && "group-hover:opacity-0",
					)}
				>
					{hasCustomizations && (
						<Badge variant="green" size="sm">
							Custom
						</Badge>
					)}
					{priceProduct && (
						<span className="text-xs text-tertiary-foreground tabular-nums">
							<PriceDisplay
								product={priceProduct}
								currency={org?.default_currency ?? "USD"}
							/>
						</span>
					)}
				</div>
				<div
					className={cn(
						"absolute right-0 flex items-center gap-1 transition-opacity duration-150",
						isLocked ? "opacity-100" : "opacity-0 group-hover:opacity-100",
					)}
				>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-tertiary-foreground hover:text-foreground"
						onClick={handleEditClick}
						disabled={isLocked}
					>
						<PencilSimpleIcon size={13} />
					</Button>
					<button
						type="button"
						className="p-1 text-subtle hover:text-destructive transition-colors disabled:pointer-events-none disabled:opacity-50"
						onClick={() => handleRemovePlan({ phaseIndex, planIndex })}
						disabled={isLocked}
					>
						<XIcon size={13} />
					</button>
				</div>
			</div>
		</div>
	);

	return row;
}
